import { IPhoneCall, IPhoneCallCategory, ITranscription } from '@ViewModels';
import {
	DialerClientType,
	ICompany,
	ImpersonationBroker,
	IOperationResult,
	IUserSessionContext,
} from '../../extViewmodels';
import { IMeme } from '../viewModels/gamification';
import { IIntegrationPipelineItem, IIntegrationPropertyConfig, ILeadView } from '../viewModels/leads/interfaces';
import { IPhoneCallFilterCriteria } from './../viewModels/phonecall';

interface IPhoneCallRequest {
	phoneNumber: string;
	companyId: string;
	contactIds?: string[];
	preferredTelephonyConfigurationId?: string;
	clientType?: DialerClientType;
	aiConversationId?: string;
}

/**
 * Diagnostic payload describing why the browser softphone ended a call.
 * Mirrors `ClientCallEndReportRequest` on the API.
 */
interface IClientCallEndReport {
	cause: string;
	reason: string;
	hadActiveCall: boolean;
	callSid?: string;
	callStatus?: string;
	direction?: string;
	deviceState?: string;
	tabId?: string;
	tabVisibility?: string;
	errorCode?: number;
}

interface IClientCallEvent {
	event: string;
	callSid?: string;
	errorCode?: number;
	message?: string;
	durationMs?: number;
	tabId?: string;
	tabVisibility?: string;
}

/**
 * Envelope for any product-usage interaction worth reporting. One endpoint serves every
 * event, so `eventName` is what separates them in the warehouse and is the only required
 * field. Nothing is enriched or validated server-side: the resource is not loaded, so
 * analytics joins on `resourceId` for anything the event does not carry itself.
 */
export interface IClientEventReport {
	eventName: string;
	/**
	 * Event-specific detail, and the one field whose keys the client chooses. It must never
	 * carry PII: these land in New Relic, which is retained, not account-scoped, and cannot
	 * be purged for a single contact. Numbers and booleans measure an interaction without
	 * identifying a person, so the API takes only those and drops strings silently — the
	 * interaction still records, the text is simply gone. Narrowed here as well so a string
	 * fails the build instead of vanishing after the fact.
	 *
	 * Nothing in here reaches the warehouse, ever: the warehouse holds the envelope and New
	 * Relic holds the detail. A measurement that has to be queryable in the warehouse needs
	 * an envelope field or a purpose-built feed — do not design a payload expecting these to
	 * be promoted later.
	 */
	properties?: Record<string, number | boolean>;
	resourceId?: string;
	resourceType?: string;
	surface?: string;
	tabId?: string;
	tabVisibility?: string;
	trigger?: string;
}

interface ISaveEmailOutcomeRequest {
	bulkEmailMessageId: string;
	leadServedSource: string;
}

interface ISaveOutcomeResponse {
	meme: IMeme;
}

export class ApiClient extends ImpersonationBroker {
	private userSession: IUserSessionContext;

	constructor(userSession: IUserSessionContext) {
		super();

		this.userSession = userSession;
	}

	public async getUserById<T>(userId: string): Promise<IOperationResult<T>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<T>(
			this.composeApiUrl({ urlPath: `user/${userId}` }),
			'GET'
		);
	}

	public async getTwilioAccessToken(): Promise<IOperationResult<string>> {
		const clientName = 'Web Browser';
		const path = `twilio/token?clientName=${clientName}`;
		return this.userSession.webServiceHelper.callWebServiceAsync<string>(path, 'GET');
	}

	public async createPhoneCall(request: IPhoneCallRequest): Promise<IOperationResult<IPhoneCall>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<IPhoneCall>(
			this.composeApiUrl({ urlPath: 'phonecall' }),
			'POST',
			request
		);
	}

	/**
	 * Report why the softphone ended a call. Diagnostics only — the server just logs it,
	 * because Twilio can prove the client disconnected but never why, and EventLogger
	 * does not reach New Relic.
	 */
	public async reportClientCallEnd(phoneCallId: string, report: IClientCallEndReport): Promise<IOperationResult<void>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<void>(
			this.composeApiUrl({ urlPath: `phonecall/${phoneCallId}/clientEndReport` }),
			'POST',
			report
		);
	}

	public async reportClientCallEvent(phoneCallId: string, event: IClientCallEvent): Promise<IOperationResult<void>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<void>(
			this.composeApiUrl({ urlPath: `phonecall/${phoneCallId}/clientCallEvent` }),
			'POST',
			event
		);
	}

	/**
	 * Report a product-usage interaction the server has no other way to see — a recording
	 * play, for instance, is served straight from a pre-signed S3 url and never reaches us.
	 * Best-effort telemetry: never gate the interaction itself on it.
	 */
	public async reportClientEvent(report: IClientEventReport): Promise<IOperationResult<void>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<void>(
			this.composeApiUrl({ urlPath: 'clientEvent' }),
			'POST',
			report,
			null,
			// Opts out of re-auth: a 401 here would otherwise refresh the token, and a refresh that
			// comes back 403 clears the credential store — logging the rep out on the strength of a
			// telemetry ping. Losing the event when the token has expired is the correct trade for
			// something best-effort; a real user action will refresh it soon enough.
			false
		);
	}

	public async getPhoneCallById(id: string): Promise<IOperationResult<IPhoneCall>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<IPhoneCall>(
			this.composeApiUrl({ urlPath: `phonecall/${id}` }),
			'GET'
		);
	}

	public async getPhoneCall(criteria: IPhoneCallFilterCriteria[]): Promise<IOperationResult<IPhoneCall>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<IPhoneCall>(
			this.composeApiUrl({ urlPath: `phonecall/filter` }),
			'POST',
			{ criteria }
		);
	}

	public async getPhoneCallCategory(): Promise<IOperationResult<IPhoneCallCategory>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<IPhoneCallCategory>(
			this.composeApiUrl({ urlPath: `phonecall/category` }),
			'GET'
		);
	}

	public async getTranscription<T>(transcriptionId: string, viewSpeakerLine?: boolean): Promise<IOperationResult<T>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<T>(
			this.composeApiUrl({
				queryParams: { type: viewSpeakerLine ? 'SpeakerLine' : undefined },
				urlPath: `transcription/${transcriptionId}${viewSpeakerLine ? '/view' : ''}`,
			}),
			'GET'
		);
	}

	public async markAsRead(transcriptionId: string): Promise<IOperationResult<ITranscription>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<ITranscription>(
			this.composeApiUrl({ urlPath: `transcription/${transcriptionId}/markRead` }),
			'POST',
			{}
		);
	}

	public async createTranscription(phoneCallId: string): Promise<IOperationResult<ITranscription>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<ITranscription>(
			this.composeApiUrl({ urlPath: `transcription` }),
			'POST',
			{
				phoneCallId,
			}
		);
	}

	public async updatePhoneCallConversationId(
		phoneCallId: string,
		conversationId: string
	): Promise<IOperationResult<IPhoneCall>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<IPhoneCall>(
			this.composeApiUrl({ urlPath: `lead/practiceCall/${phoneCallId}/conversationId` }),
			'PATCH',
			{
				conversationId,
			}
		);
	}

	public async notifyPracticeCallEnded(phoneCallId: string): Promise<IOperationResult<void>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<void>(
			this.composeApiUrl({ urlPath: `lead/practiceCall/${phoneCallId}/complete` }),
			'POST',
			{}
		);
	}

	public async getLead(companyId: string): Promise<IOperationResult<ILeadView>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<ILeadView>(
			this.composeApiUrl({ urlPath: `lead/${companyId}` }),
			'GET'
		);
	}

	public async getDealPipeline(): Promise<IOperationResult<IIntegrationPipelineItem>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<IIntegrationPipelineItem>(
			this.composeApiUrl({ urlPath: `aida/property/deal/pipeline` }),
			'GET'
		);
	}

	public async getLeadConfig(): Promise<IOperationResult<IIntegrationPropertyConfig>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<IIntegrationPropertyConfig>(
			this.composeApiUrl({ urlPath: `aida/property/lead/config` }),
			'GET'
		);
	}

	public async getDealConfig(): Promise<IOperationResult<IIntegrationPropertyConfig>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<IIntegrationPropertyConfig>(
			this.composeApiUrl({ urlPath: `aida/property/deal/config` }),
			'GET'
		);
	}

	public async updateLeadConfig(request: any): Promise<IOperationResult<IIntegrationPropertyConfig>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<IIntegrationPropertyConfig>(
			this.composeApiUrl({ urlPath: `aida/property/lead/config` }),
			'PATCH',
			request
		);
	}

	public async updateDealConfig(request: any): Promise<IOperationResult<IIntegrationPropertyConfig>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<IIntegrationPropertyConfig>(
			this.composeApiUrl({ urlPath: `aida/property/deal/config` }),
			'PATCH',
			request
		);
	}

	public async getCompany(companyId: string): Promise<IOperationResult<ICompany>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<ICompany>(
			this.composeApiUrl({ urlPath: `company/${companyId}` }),
			'GET'
		);
	}

	public async getAvailableLeadsNumber(ruleId: string): Promise<IOperationResult<number>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<number>(
			this.composeApiUrl({ urlPath: `aida/rule/${ruleId}/count` }),
			'GET'
		);
	}

	public async saveEmailOutcome(
		companyId: string,
		request: ISaveEmailOutcomeRequest
	): Promise<IOperationResult<ISaveOutcomeResponse>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<ISaveOutcomeResponse>(
			this.composeApiUrl({ urlPath: `lead/${companyId}/EmailOutcome` }),
			'POST',
			request
		);
	}

	public async getVoicemailText(): Promise<IOperationResult<string>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<string>(
			this.composeApiUrl({ urlPath: `twilio/voicemailtext` }),
			'GET'
		);
	}

	public async updateVoicemailText(message: string): Promise<IOperationResult<string>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<string>(
			this.composeApiUrl({ urlPath: `twilio/voicemailtext` }),
			'POST',
			{
				text: message,
			}
		);
	}

	public async getDemoRecordings<T>(demoId: string): Promise<IOperationResult<T>> {
		return this.userSession.webServiceHelper.callWebServiceAsync<T>(
			this.composeApiUrl({ urlPath: `Lead/demos/${demoId}/recordings` }),
			'GET'
		);
	}
}
